import { gsap } from "gsap";

/**
 * Intro animation (prima visita di sessione).
 *
 * VARIANTE: cambia INTRO_VARIANT per scegliere il flow.
 *  - "classic": logo center stagger + barre + reveal header pezzo-per-pezzo
 *  - "soft":    schermo bianco + contatore 0→90 + logo fluttuante,
 *               poi pannello slide-up e header crossfade
 */
const INTRO_VARIANT = "soft"; // "classic" | "soft"

// Applica subito la classe sul body se variant=soft (evita flash di sfondo
// nero prima che JS sia stato eseguito completamente)
if (INTRO_VARIANT === "soft" && typeof document !== "undefined") {
  // Aspetta che <body> esista (il modulo può essere parsato prima che il DOM
  // sia pronto se il bundle è caricato nell'<head>)
  if (document.body) {
    document.body.classList.add("intro-soft");
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      document.body.classList.add("intro-soft");
    });
  }
}

const STORAGE_KEY = "intro_played";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const cleanupIntro = (overlay) => {
  document.body.classList.remove("intro-playing");
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
  window.dispatchEvent(new CustomEvent("intro-complete"));
  // [TEST] sessionStorage disabilitato — riattivare prima del deploy.
  try {
    sessionStorage.setItem(STORAGE_KEY, "true");
  } catch (e) {
    // sessionStorage disabilitato: l'intro ripartirà al prossimo load. Non blocca.
  }
};

const skipIntro = (overlay) => {
  document.body.classList.remove("intro-playing");
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
  window.dispatchEvent(new CustomEvent("intro-complete"));
};

const runReducedMotionIntro = (overlay) => {
  gsap.to(overlay, {
    autoAlpha: 0,
    duration: 0.3,
    ease: "power1.out",
    onComplete: () => cleanupIntro(overlay),
  });
};

const runFullIntro = (overlay) => {
  const logoCenter = overlay.querySelector(".intro-overlay__logo");
  const logoParts = overlay.querySelectorAll(".intro-overlay__logo-part");
  const screen = overlay.querySelector(".intro-overlay__screen");
  const bars = overlay.querySelectorAll(".intro-overlay__bar");
  const logoHeader = document.querySelector('[data-reveal="logo"]');
  const menuItems = document.querySelectorAll('[data-reveal="menu-item"]');
  const langSection = document.querySelector('[data-reveal="lang"]');
  const dividerH = document.querySelector('[data-reveal="divider-h"]');
  const dividerV1 = document.querySelector('[data-reveal="divider-v1"]');
  const dividerV2 = document.querySelector('[data-reveal="divider-v2"]');

  const tl = gsap.timeline({
    onComplete: () => cleanupIntro(overlay),
  });

  // ============================================================================
  // Stati di partenza espliciti (allineati a intro-overlay.scss)
  // ============================================================================
  tl.set(logoParts, { opacity: 0, scale: 0.85, transformOrigin: "center center" }, 0);
  tl.set(bars, { yPercent: -100, visibility: "visible" }, 0);
  if (logoHeader) tl.set(logoHeader, { opacity: 0 }, 0);
  if (menuItems.length) tl.set(menuItems, { opacity: 0 }, 0);
  if (langSection) tl.set(langSection, { opacity: 0 }, 0);
  if (dividerH) tl.set(dividerH, { scaleX: 0 }, 0);
  if (dividerV1) tl.set(dividerV1, { scaleY: 0 }, 0);
  if (dividerV2) tl.set(dividerV2, { scaleY: 0 }, 0);

  // ============================================================================
  // FASE 1: logo center a stagger (T=0.20 → T=0.95) + barre (T=1.60 → ...)
  // ============================================================================

  // T=0.20 → 1.40 - 4 gruppi logo: fade in + scale 0.85→1, stagger 0.30s
  // Ordine: number-one → ago-museum → giacomo-agostini → la-leggenda (DOM order)
  tl.to(logoParts, {
    opacity: 1,
    scale: 1,
    duration: 0.5,
    ease: "power2.out",
    stagger: 0.30,
  }, 0.20);

  // T=2.30 - 4 barre calano (coprono progressivamente il logo center)
  tl.to(bars, {
    yPercent: 0,
    duration: 1.0,
    ease: "power3.inOut",
    stagger: 0.08,
  }, 2.30);

  // T=3.10 - Logo center off istantaneo (è già coperto dalle barre)
  tl.set(logoCenter, { opacity: 0 }, 3.10);

  // T=3.20 - Fondo nero screen svanisce (nascosto dalle barre)
  tl.to(screen, {
    opacity: 0,
    duration: 0.3,
    ease: "none",
  }, 3.20);

  // T=3.50 - Ritiro barre verso il basso
  tl.to(bars, {
    yPercent: 100,
    duration: 0.7,
    ease: "power2.inOut",
    stagger: 0.08,
  }, 3.50);

  // ============================================================================
  // FASE 2: catena reveal header (T=3.50 → T=6.40)
  // Bordo orizzontale continuo lento, divisorie + contenuti intrecciati
  // ============================================================================

  // T=3.50 - Bordo orizzontale: linea continua SX → DX (1.8s, lineare)
  if (dividerH) {
    tl.to(dividerH, {
      scaleX: 1,
      duration: 1.8,
      ease: "none",
    }, 3.50);
  }

  // T=3.95 (bordo al 25%) - Divisoria V1 dal basso (0.45s)
  if (dividerV1) {
    tl.to(dividerV1, {
      scaleY: 1,
      duration: 0.45,
      ease: "power2.out",
    }, 3.95);
  }

  // T=4.10 (V1 a +0.15s) - Logo header fade in (intrecciato con V1)
  if (logoHeader) {
    tl.to(logoHeader, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    }, 4.10);
  }

  // T=4.85 (bordo al 75%) - Divisoria V2 dal basso (0.45s)
  if (dividerV2) {
    tl.to(dividerV2, {
      scaleY: 1,
      duration: 0.45,
      ease: "power2.out",
    }, 4.85);
  }

  // T=5.00 (V2 a +0.15s) - Menu: 4 voci fade + stagger (intrecciato con V2)
  if (menuItems.length) {
    tl.to(menuItems, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.10,
    }, 5.00);
  }

  // T=5.90 (menu completato a 5.80) - Lang switcher fade in (0.5s)
  if (langSection) {
    tl.to(langSection, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    }, 5.90);
  }
};

/**
 * Variante "soft" — sequenza narrata in 4 atti.
 *
 *  ATTO 1 — Screen bianco
 *  T=0.0  Schermo bianco (pausa contemplativa)
 *
 *  ATTO 2 — Logo entra
 *  T=0.6  Logo: fade-in (opacity 0→1) + slide-up (y +30 → 0)
 *         1.2s, power2.out
 *  T=2.0  Logo inizia a fluttuare (loop sine ±3px Y)
 *
 *  ATTO 3 — Logo esce
 *  T=3.5  Fluttuazione killata
 *  T=3.5  Logo: fade-out + slide-up (y 0 → -30)
 *         1.0s, power2.in
 *
 *  ATTO 4 — Pannello esce + header reveal (T=4.5 → T=6.3)
 *  T=4.5  Dispatch "intro-complete" → hero parte sotto
 *  T=4.5  Pannello: slide-up (yPercent 0 → -100), 1.8s, expo.in
 *  T=4.7  Pannello: fade (opacity 1→0), 1.2s, power1.inOut
 *  T=4.5  Header logo+menu+bordi: crossfade fade-in (1.0s, power2.out)
 *  T=4.5  Lang switcher: yPercent -100→0 (1.0s, power2.out)
 *  T=6.3  Cleanup
 */
const runSoftIntro = (overlay) => {
  // classe intro-soft già aggiunta al body in cima al modulo
  const screen = overlay.querySelector(".intro-overlay__screen");
  const logo = overlay.querySelector(".intro-overlay__logo");
  const logoParts = overlay.querySelectorAll(".intro-overlay__logo-part");
  const counter = overlay.querySelector("[data-intro-counter]");
  const logoHeader = document.querySelector('[data-reveal="logo"]');
  const menuItems = document.querySelectorAll('[data-reveal="menu-item"]');
  const langSection = document.querySelector('[data-reveal="lang"]');
  const dividerH = document.querySelector('[data-reveal="divider-h"]');
  const dividerV1 = document.querySelector('[data-reveal="divider-v1"]');
  const dividerV2 = document.querySelector('[data-reveal="divider-v2"]');

  let floatTween = null; // riferimento al tween di fluttuazione (da killare prima dell'uscita)

  const tl = gsap.timeline({
    onComplete: () => {
      cleanupIntro(overlay);
      document.body.classList.remove("intro-soft");
    },
  });

  // Stati di partenza
  // Il logo entra dal basso: lo parto a y +30 con i 4 gruppi tutti opacity 0
  tl.set(logo, { y: 30 }, 0);
  tl.set(logoParts, { opacity: 0, scale: 1, transformOrigin: "center center" }, 0);
  if (counter) tl.set(counter, { display: "none" }, 0); // non usato in questa variante
  if (logoHeader) tl.set(logoHeader, { opacity: 0 }, 0);
  if (menuItems.length) tl.set(menuItems, { opacity: 0 }, 0);
  if (langSection) tl.set(langSection, { opacity: 0, yPercent: -100 }, 0);
  if (dividerH) tl.set(dividerH, { opacity: 0, scaleX: 1 }, 0);
  if (dividerV1) tl.set(dividerV1, { opacity: 0, scaleY: 1 }, 0);
  if (dividerV2) tl.set(dividerV2, { opacity: 0, scaleY: 1 }, 0);

  // ATTO 2 — T=0.6: logo entra in 3 step stagger 0.4s
  // 1) ago-museum + number-one (insieme, sono "AGO1MUSEUM")
  // 2) giacomo-agostini
  // 3) la-leggenda
  // Movimento: ogni gruppo entra dal basso con fade (y +30 → 0)
  const partNumberOne = overlay.querySelector('[data-logo-part="number-one"]');
  const partAgoMuseum = overlay.querySelector('[data-logo-part="ago-museum"]');
  const partGiacomo = overlay.querySelector('[data-logo-part="giacomo-agostini"]');
  const partLeggenda = overlay.querySelector('[data-logo-part="la-leggenda"]');

  // Stato iniziale: il movimento y va sui singoli gruppi (non sul wrapper)
  //tl.set([partNumberOne, partAgoMuseum, partGiacomo, partLeggenda].filter(Boolean), { y: 130 }, 0);
  tl.set(logo, { y: 0 }, 0);

  // Step 1 (T=0.6): ago-museum + number-one insieme
  tl.to([partAgoMuseum, partNumberOne].filter(Boolean), {
    opacity: 1,
   // y: 0,
    duration: 1.2,
    ease: "expo.in",
  }, 0.6);

  // Step 2 (T=1.0): giacomo-agostini
  if (partGiacomo) {
    tl.to(partGiacomo, {
      opacity: 1,
       // y: 0,
      duration: 1.2,
      ease: "expo.in",
    }, 1.0);
  }

  // Step 3 (T=1.4): la-leggenda
  if (partLeggenda) {
    tl.to(partLeggenda, {
      opacity: 1,
      //y: 0,
      duration: 1.2,
      ease: "expo.in",
    }, 1.4);
  }

  // T=2.0: logo inizia a fluttuare (gestito fuori dalla timeline,
  // ma salvo il riferimento per killarlo prima dell'uscita)
  // tl.call(() => {
  //   if (logo) {
  //     floatTween = gsap.to(logo, {
  //       y: -3,
  //       duration: 1.5,
  //       ease: "expo.in",
  //       yoyo: true,
  //       repeat: -1,
  //     });
  //   }
  // }, null, 2.0);

  // ATTO 3 — T=3.5: logo esce verso l'alto con fade
  // Prima killiamo la fluttuazione per evitare conflitto col tween di uscita
  // tl.call(() => {
  //   if (floatTween) {
  //     floatTween.kill();
  //     floatTween = null;
  //     gsap.set(logo, { y: 0 }); // reset position prima del tween di uscita
  //   }
  // }, null, 3.5);

  // Step 1 (T=3.5): la-leggenda esce per prima
  if (partLeggenda) {
    tl.to(partLeggenda, {
      opacity: 0,
      y: -10,
      duration: 1.0,
      ease: "expo.out",
    }, 3.5);
  }

  // Step 2 (T=3.9): giacomo-agostini
  if (partGiacomo) {
    tl.to(partGiacomo, {
      opacity: 0,
      y: -10,
      duration: 1.0,
      ease: "expo.out",
    }, 3.9);
  }

  // Step 3 (T=4.3): ago-museum + number-one (insieme, ultimi a uscire)
  tl.to([partAgoMuseum, partNumberOne].filter(Boolean), {
    opacity: 0,
    y: -10,
    duration: 1.0,
    ease: "expo.out",
  }, 4.3);
  

  // ATTO 4 — T=4.5: dispatch intro-complete (hero parte sotto)
  tl.call(() => {
    window.dispatchEvent(new CustomEvent("intro-complete"));
  }, null, 4.5);

  // T=4.5: pannello slide-up con fade
  if (screen) {
    tl.to(screen, {
      yPercent: -110,
      duration: 1.8,
      ease: "expo.in",
    }, 4.3);

    tl.to(screen, {
      yPercent: -110,
      opacity:0,
      duration: 1.8,
      ease: "expo.in",
    }, 4.8);
  }

  // T=4.5: header reveal in crossfade
  if (logoHeader) {
    tl.to(logoHeader, { opacity: 1, duration: 1.0, ease: "power2.out" }, 4.5);
  }
  if (menuItems.length) {
    tl.to(menuItems, { opacity: 1, duration: 1.0, ease: "power2.out" }, 4.5);
  }
  if (dividerH) {
    tl.to(dividerH, { opacity: 1, duration: 1.0, ease: "power2.out" }, 4.5);
  }
  if (dividerV1) {
    tl.to(dividerV1, { opacity: 1, duration: 1.0, ease: "power2.out" }, 4.5);
  }
  if (dividerV2) {
    tl.to(dividerV2, { opacity: 1, duration: 1.0, ease: "power2.out" }, 4.5);
  }

  // T=4.5: lang switcher cala dall'alto
  if (langSection) {
    tl.to(langSection, {
      opacity: 1,
      yPercent: 0,
      duration: 1.0,
      ease: "power2.out",
    }, 4.5);
  }
};

const initIntroAnimation = () => {
  const overlay = document.querySelector("[data-intro-overlay]");
  if (!overlay) return;

  // [TEST] sessionStorage disabilitato per testare l'animazione ad ogni reload.
  // Riattivare prima del deploy.
  let alreadyPlayed = false;
  try {
    alreadyPlayed = sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch (e) {
    alreadyPlayed = false;
  }
  
  if (alreadyPlayed) {
    skipIntro(overlay);
    return;
  }

  document.body.classList.add("intro-playing");

  if (prefersReducedMotion()) {
    runReducedMotionIntro(overlay);
    return;
  }

  if (INTRO_VARIANT === "soft") {
    runSoftIntro(overlay);
  } else {
    runFullIntro(overlay);
  }
};

/**
 * Sticky header con detection direzione scroll.
 */
const stickyHeaderLogic = () => {
  const header = document.querySelector(".header");
  if (!header) return;

  let lastScrollTop = 0;
  let ticking = false;

  const updateScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > 100) {
      header.classList.add("is-scrolled");
    } else {
      header.classList.remove("is-scrolled");
    }

    if (scrollTop > lastScrollTop) {
      header.classList.remove("is-scrolling-up");
      header.classList.add("is-scrolling-down");
    } else {
      header.classList.remove("is-scrolling-down");
      header.classList.add("is-scrolling-up");
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    ticking = false;
  };

  window.addEventListener("scroll", () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScroll);
      ticking = true;
    }
  });
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initIntroAnimation);
  } else {
    initIntroAnimation();
  }
  stickyHeaderLogic();
};